import { AccountTypeDetails } from './api/account';
import { PEPAnswer, PEPAnswerStatus } from './api/pep';
import { Tenant } from './common';
import { Document } from './document';

export enum ApplicationType {
  INDIVIDUAL = 'INDIVIDUAL',
  JOINT = 'JOINT',
  OTHER = 'OTHER',
  BUSINESS = 'BUSINESS',
  SHARED_WITH_SOMEONE_ELSE = 'SHARED_WITH_SOMEONE_ELSE',
}

export enum UserTitle {
  MR = 43,
  MS = 46,
  MRS = 44,
  DR = 45,
}

export enum ClientType {
  New = 'DO',
  Existing = 'EXC',
  MobileAdoption = 'mobile-adoption',
}

export enum OperationType {
  New = 'DO',
  Existing = 'ExC',
  Remediation = 'REMEDIATION',
}

export enum UserStatus {
  Draft = 'DRAFT',
  Approved = 'APPROVED',
  PreApproved = 'PRE_APPROVED',
  Opened = 'OPENED',
  Rejected = 'REJECTED',
  InReview = 'IN_REVIEW',
  Archived = 'ARCHIVED',
  PendingApproval = 'PENDING_APPROVAL',
  Closing = 'CLOSING',
  Close = 'CLOSED',
}
export enum EmploymentStatus {
  Employed = 'EMPLOYED',
  SelfEmployed = 'SELF_EMPLOYED',
  Unemployed = 'UNEMPLOYED',
}

export enum Profession {
  SoleTrader = 'Sole_Trader',
  PartnershipLimitedLiability = 'Partner_in_a_Partnership',
  Unincorporated = 'Sole_Trader_Unregistered',
  Student = 'Student',
  Pensioner = 'Pensioner',
  Housewife = 'Housewife',
  Unemployed = 'Unemployed',
  CYM_PartnershipLimitedLiability = 'Partnership_Limited_Liability',
  CYM_Unincorporated = 'Unincorporated',
  Employee = 'Employee',
  Director = 'Director',
}

export enum FundingSource {
  Salary = 'SALARY',
  Savings = 'SAVINGS',
  FundingFromSpouse = 'FUNDING_FROM_SPOUSE',
  FundingFromParents = 'FUNDING_FROM_PARENTS',
  BusinessProceeds = 'BUSINESS_PROCEEDS',
  Investments = 'INVESTMENTS',
  SaleOfAsset = 'SALE_OF_ASSET',
  Inheritance = 'INHERITANCE',
  RentalIncome = 'RENTAL_INCOME',
  Dividends = 'DIVIDENDS',
  Scholarship = 'SCHOLARSHIP',
  OtherIncome = 'OTHER',
  Business = 'BUSINESS',
  FundsFromParents = 'FUNDS_FROM_PARENTS',
  FundsFromSpouse = 'FUNDS_FROM_SPOUSE',
  Pension = 'PENSION',
}

export enum IncomeSource {
  Salary = 'Salary',
  SelfEmployment = 'SelfEmployment',
  Business = 'Business',
  Investment = 'Investment',
  Rental = 'Rental',
  Pension = 'Pension',
  GovernmentBenefits = 'GovernmentBenefits',
  Savings = 'Savings',
  Remittances = 'Remittances',
  Scholarships = 'Scholarships',
  InsurancePayouts = 'InsurancePayouts',
  Inheritance = 'Inheritance',
  Other = 'Other',
}

export enum PayFrequency {
  Monthly = 'Monthly',
  Weekly = 'Weekly',
  Fortnightly = 'Fortnightly',
  Other = 'Other',
}

export enum ICBResponseCode {
  Success = 0,
  UsernameRequired = 1005,
  UsernameError = 5,
  GenericError = 2,
  IdentificationRequired = 1001,
  NameAndSurnameRequired = 1002,
  MobilePhoneRequired = 1003,
  ClientInformationRequired = 1004,
  BirthDateRequired = 1006,
  ExistingCustomerProfilePending = -10,
}

export enum ResidenceType {
  Own = 'Own',
  Rent = 'Rent',
  Family = 'Family',
  Company = 'Company',
  Other = 'Other',
  Empty = '',
}

export enum AddressType {
  PERMANENT = 'PERMANENT',
  MAILING = 'MAILING',
}

export type AddressDetails = {
  line1?: string;
  line2?: string;
  city?: string;
  poBox?: string;
  poBoxDistrict?: string;
  state?: string;
  town?: string;
  country?: string;
  zip?: string;
  residence?: ResidenceType;
  residenceCountry?: string;
  sameAsAdult?: boolean;
  poaRequired?: boolean;
  childLivesWithPersonName?: string;
  childLivesWithRelationship?: string;
  childLivesWithRelationshipDescription?: string;
};

export type MailingAddressPayload = {
  line1?: string;
  line2?: string;
  city?: string;
  town?: string;
  state?: string;
  zip?: string;
  country?: string;
  poBox?: string;
  poBoxDistrict?: string;
  residence?: ResidenceType;
};

export type UserAddress = AddressDetails & {
  id?: string;
  type?: AddressType;
};

export enum Gender {
  Male = 'M',
  Female = 'F',
}

export enum MaritalStatus {
  Single = 'SINGLE',
  Unmarried = 'UNMARRIED',
  Married = 'MARRIED',
  CommonLaw = 'COMMON_LAW',
  Divorced = 'DIVORCED',
  Widow = 'WIDOWED',
  Other = 'OTHER',
}

export enum HolderType {
  Individual = 'INDIVIDUAL',
  JointAdult = 'JOINT_ADULT',
  JointMinor = 'JOINT_MINOR',
  // TODO: check with BE for correct type when apply minor flow
  InvitedAdult = 'INVITED_ADULT',
}

export type EmploymentDetails = {
  employerName?: string;
  employerAddress?: string;
  employerPhone?: string;
  employerCountry?: string;
  occupationCode?: string;
  startDate?: string;
  employmentStatus?: EmploymentStatus;
  employmentDesc?: Profession | '';
  riskSectorFlag?: boolean;
  riskSectorCategory?: string | null;
  isStartup?: boolean | null;
  requireBasicInfo?: boolean;
  businessCategoryCode?: string | null;
  poeRequired?: boolean;
};

export type UserAccountType = { currency: string } & Pick<
  AccountTypeDetails,
  | 'id'
  | 'name'
  | 'operationId'
  | 'primaryAccount'
  | 'secondaryAccount'
  | 'accountNo'
  | 'status'
  | 'type'
  | 'feeCurrency'
  | 'estimatedMonthlyDepositAmount'
  | 'estimatedMonthlyDepositCount'
  | 'estimatedMonthlyCashDepositAmount'
  | 'willSendReceiveFromOthers'
  | 'willUseInternationalWireTransfers'
  | 'isBusinessPurpose'
  | 'fundingSource'
  | 'fundingSourceUpdatedInRemediation'
  | 'estimatedMonthlyDepositAmountUpdatedInRemediation'
> & { classCode?: string };

export type User = {
  id: number | string;
  email: string;
  position?: string;
  contact?: string;
  applicationType?: ApplicationType;
  documents: Document[];
  birthday?: string;
  nationality?: string;
  citizenship?: string;
  citizenship2?: string;
  country?: string;
  adultPickup?: boolean;
  adultEmail?: string;
  holderType?: string;
  countryOfBirth?: string;
  firstName?: string;
  middleName?: string;
  lastName?: string;
  title?: string;
  gender?: Gender;
  maritalStatus?: MaritalStatus;
  status: UserStatus;
  username?: string | null;
  address?: string;
  addressDetailed: AddressDetails;
  addresses?: UserAddress[];
  taxResidences?: string;
  employmentDetailed?: EmploymentDetails;
  riskSectorFlag?: boolean;
  pepAnswerStatus?: PEPAnswerStatus;
  pepAnswered?: string[];
  answerId?: string;
  lastAnswer?: {
    answers: PEPAnswer[];
  };
  icbResponseCode?: ICBResponseCode;
  accountTypes?: UserAccountType[];
  rim: string;
  phoneNumber: string;
  phoneNumberAlt?: string;
  picture?: string;
  termsAndConditions?: boolean;
  usIndicia?: boolean;
  citizenship2Has?: boolean;
  motherMaidenName?: string | null;
  territoryCode: string;
  residenceCountry?: string;
  branchName: string;
  visitingBranchId?: string;
  tenant: Tenant;
  eligible?: boolean;
  monthlyDeposit?: number;
  usCitizenshipRenouncement?: boolean;
  totalAnnualDeposit?: string;
  monthlyDepositCurrency?: string;
  monthlyIncome?: number;
  monthlyIncomeCurrency?: string;
  fundingSource?: string;
  incomeSources?: {
    sourceType: string;
    amount: number;
    currency: string;
    description?: string;
  }[];
  sourceOfIncome?: string; // JSON stringified array of IncomeSource
  sourceOfIncomeOther?: string;
  howOftenPaid?: PayFrequency;
  howOftenPaidOther?: string;
  totalMonthlyIncome?: number;
  totalMonthlySpending?: number;
  infoAccuracyConfirmed?: boolean;
  tin?: Record<string, string>;
  language?: string;
  type?: OperationType;
  clientReviewed?: boolean;
  operationId?: string;
  chargeAccountNo?: string;
  cityOfBirth?: string | null;
  isHighRisk?: boolean;
  adultConfirmKyc?: boolean;
  // Minor consent fields
  consentSignDocuments?: boolean;
  consentInternetBanking?: boolean;
  consentManageAccount?: boolean;
  minorPhoneNumber?: string;
  hasPhoneNumber?: boolean;
  relationshipToAdult?: string;
  allowToSign?: boolean;
  allowToManage?: boolean;
  clientType?: ClientType;
  biometricConsent?: boolean | null;
  requireIdentity?: boolean;
  confirmPersonalDetails?: boolean;
  allowToDebitCardOnlineBanking?: boolean;
  hasLiability?: boolean;
};

export type Dictionary = {
  code: string;
  value: string;
};

export type Occupation = Dictionary;

export type BusinessCategory = Dictionary;

export type UsernameValidationResult = {
  username: string;
  isValid: boolean;
};

export type AccountFormData = {
  monthlyDepositAmount?: string;
  monthlyDepositsCount?: string;
  monthlyCashDepositAmount?: string;
  sendReceiveMoney?: boolean;
  internationalWireTransfers?: boolean;
  isBusinessPurpose?: boolean;
};
